/**
 * Pre-signup campaign handoff from the public site (creatorboost.ai).
 *
 * Query params (utm_*, click ids, ph_distinct_id) are stashed in sessionStorage
 * on first touch (no TTL). Survives Google OAuth. Cleared after PostHog identify
 * / signup provisioning (see posthog-provider + UserContext).
 */

export const UTM_STORAGE_KEY = 'nf_utm_params'
export const PH_DISTINCT_ID_KEY = 'nf_ph_distinct_id'

/** Campaign + click-id keys accepted from public-site handoff URLs. */
export const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'ref',
  'via',
  'gclid',
  'fbclid',
  'twclid',
  'gbraid',
  'wbraid',
  'li_fat_id',
  'ttclid',
  'rdt_cid',
]

const MAX_VALUE_LENGTH = 500

function sanitize(value) {
  if (value == null) return ''
  let cleaned = ''
  for (const char of String(value)) {
    const code = char.charCodeAt(0)
    cleaned += code <= 31 || code === 127 ? ' ' : char
  }
  return cleaned.replace(/\s+/g, ' ').trim().slice(0, MAX_VALUE_LENGTH)
}

function readJson(key) {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function writeJson(key, value) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // non-fatal
  }
}

/**
 * Parse search string into a plain attribution object (no ph_distinct_id).
 * @returns {{ params: Record<string, string>, phDistinctId: string|null }}
 */
export function parseAttributionSearch(search = '') {
  const qs = search.startsWith('?') ? search.slice(1) : search
  const sp = new URLSearchParams(qs)
  const params = {}
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const v = sanitize(sp.get(key))
    if (v) params[key] = v
  }
  const phDistinctId = sanitize(sp.get('ph_distinct_id')) || null
  return { params, phDistinctId }
}

/**
 * First-touch stash from the current URL (or given search). Does not overwrite
 * existing sessionStorage values.
 */
export function captureAttributionFromUrl(search) {
  if (typeof window === 'undefined') return
  const resolved =
    search != null ? search : window.location.search || ''
  const { params, phDistinctId } = parseAttributionSearch(resolved)

  if (Object.keys(params).length && !getStoredUtmParams()) {
    writeJson(UTM_STORAGE_KEY, params)
  }

  if (phDistinctId && !getStoredPhDistinctId()) {
    try {
      window.sessionStorage.setItem(PH_DISTINCT_ID_KEY, phDistinctId)
    } catch {
      // non-fatal
    }
  }
}

/** @returns {Record<string, string>|null} */
export function getStoredUtmParams() {
  const parsed = readJson(UTM_STORAGE_KEY)
  if (!parsed || typeof parsed !== 'object') return null
  const out = {}
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const v = sanitize(parsed[key])
    if (v) out[key] = v
  }
  return Object.keys(out).length ? out : null
}

export function getStoredPhDistinctId() {
  if (typeof window === 'undefined') return null
  try {
    return sanitize(window.sessionStorage.getItem(PH_DISTINCT_ID_KEY)) || null
  } catch {
    return null
  }
}

export function clearPhDistinctId() {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(PH_DISTINCT_ID_KEY)
  } catch {
    // non-fatal
  }
}

export function clearUtmParams() {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(UTM_STORAGE_KEY)
  } catch {
    // non-fatal
  }
}

/** Clear all pre-signup handoff keys after identify / signup. */
export function clearAttributionHandoff() {
  clearUtmParams()
  clearPhDistinctId()
}

/**
 * Sanitize a client-supplied utmParams object for API / activity_data.
 * @returns {Record<string, string>}
 */
export function sanitizeUtmParamsForApi(raw) {
  if (!raw || typeof raw !== 'object') return {}
  const out = {}
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const v = sanitize(raw[key])
    if (v) out[key] = v
  }
  const ph = sanitize(raw.ph_distinct_id || raw.phDistinctId)
  if (ph) out.ph_distinct_id = ph
  return out
}
